import React, { useEffect, useMemo, useState } from "react";

import { Job, JobAllocation, JobService } from "models/job/job";
import { GetJobs } from "api/job";
import LoadingComponent from "common/components/loader";
import TableContainer from "common/table-container";
import BreadCrumb from "common/breadcrumb";
import { Pencil, Search } from "lucide-react";
import { RoutePath } from "routes/route-path";
import { Link, useNavigate } from "react-router-dom";
import { JobStatuses } from "models/settings/branding";
import PagedTableContainer from "common/paged-table-container";

const JobListPage: React.FC = () => {
  const title = "Job List";
  const pageTitle = "Job Management";

  const navigate = useNavigate();

  const [data, setData] = useState<Job[]>([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState<string>("");
  const [searchVehicleText, setSearchVehicleText] = useState<string>("");
  const onSearchClick = async () => {
    const _param = {
      searchText: searchText,
    };
    if (currentPage != 1) {
      setCurrentPage(1);
    } else {
      await loadJobs();
    }
  };
  useEffect(() => {
    if (currentPage) {
      console.log("page:" + currentPage);
      loadJobs();
    }
  }, [currentPage]);

  const loadJobs = async () => {
    setLoading(true);
    var param: any = {};
    if (searchText) {
      param["Customer"] = searchText;
    }
    if (searchVehicleText) {
      param["Vehicle"] = searchVehicleText;
    }
    const res = await GetJobs(param, undefined, pageSize, currentPage);
    if (!(res?.status?.isError ?? false) && res?.data) {
      setData(res?.data ?? []);
      setTotal(res?.totalResultCount ?? 0);
    } else {
      setData([]);
      setTotal(0);
    }
    setLoading(false);
  };

  // Get Data
  /*   useEffect(() => {
    loadJobs();
  }, []);
 */
  const handleEditClick = (id: string) => {
    navigate(`${RoutePath.Admin_Job_edit.replace("/:id", "/" + id)}`);
  };

  const getJobStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "bg-yellow-500";
      case "APPROVAL_PENDING":
        return "bg-blue-500";
      case "APPROVED":
        return "bg-blue-700";
      case "ONGOING":
        return "bg-orange-500";
      case "COMPLETED":
        return "bg-green-500";
      case "CANCELLED":
        return "bg-gray-500";
      case "PAYMENT_PENDING":
        return "bg-red-300";
      case "PAID":
        return "bg-custom-500";
      default:
        return "bg-gray-500"; // default color
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Payment Status",
        accessorKey: "",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const jobStatus = cell.row.original.jobStatusId ?? "";
          const jobStatusColor = getJobStatusColor(jobStatus);
          // Format the totalGrossAmount to 2 decimal places with commas
          const formattedTotal = cell.row.original.totalAmount
            ? new Intl.NumberFormat().format(cell.row.original.totalAmount.toFixed(2))
            : "0.00"; // Fallback to "0.00" if no value exists

          const _invoices =
            cell.row.original.invoices && cell.row.original.invoices.length > 0
              ? cell.row.original.invoices.filter((i: any) => !(i.isCancelled ?? false))
              : [];

          const _invoice =
            cell.row.original.invoices && cell.row.original.invoices.length > 0
              ? cell.row.original.invoices[cell.row.original.invoices.length - 1]
              : null;
          const _formattedInvoiceRef = _invoices && _invoices.length > 0 ? _invoices[0].invoiceReference : "";
          return (
            <>
              <div className="text-sm whitespace-normal" style={{ display: "none" }}>
                <p>{cell.row.original.id ?? ""}</p>
              </div>
              <p>{cell.row.original.description ?? ""}</p>

              <div className={`inline-block px-4 py-1 rounded-lg text-sm text-white font-semibold ${jobStatusColor}`}>
                <span>{jobStatus}</span>
              </div>
              <p>Total: {formattedTotal}</p>
              {_formattedInvoiceRef && _formattedInvoiceRef.length > 0 && <p>Invoice Ref: {_formattedInvoiceRef}</p>}
            </>
          );
        },
      },
      {
        header: "Customer",
        accessorKey: "customer",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            <p>Customer Name: {cell.row.original.customer?.name ?? ""}</p>
            <p>Contact No: {cell.row.original.customer?.contactNumber ?? ""}</p>
            {/*   <p>Email: {cell.row.original.customer?.email ?? ""}</p> */}
          </>
        ),
      },
      {
        header: "Vehicle",
        accessorKey: "vehicle",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            <p>Vehicle No: {cell.row.original.vehicle?.vehicleNo}</p>
            <p>
              Make:{" "}
              {cell.row.original.vehicle?.manufacturer?.name ?? cell.row.original.vehicle?.model?.manufacturer?.name}
            </p>
            <p>Model: {cell.row.original.vehicle?.modelName ?? cell.row.original.vehicle?.model.name}</p>
            <p>Milage: {cell.row.original.mileage}</p>
            {/*  <p>Chassis No: {cell.row.original.vehicle?.chassisNo}</p>
            <p>Year: {cell.row.original.vehicle?.year}</p> */}
          </>
        ),
      },
      {
        header: "Services",
        accessorKey: "services",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>
            {(cell.row.original.services ?? []).map((itm: JobService, index: number) => (
              <p key={`svc_${index}`}>{itm.service?.name ?? ""}</p>
            ))}
          </>
        ),
      },
      {
        header: "Date",
        accessorKey: "date",
        enableColumnFilter: false,
        width: 10,
        cell: (cell: any) => (
          <>
            <p>{(cell.row.original.insertedTime ?? "").split("T")[0]}</p>
          </>
        ),
      },
      /*{
        header: "Assignment",
        accessorKey: "assignment",
        enableColumnFilter: false,
        cell: (cell: any) => {
          // Create a Set to store unique names
          const uniqueNames = new Set<string>();

          // Loop through allocations to extract names
          (cell.row.original.allocations ?? []).forEach((itm: JobAllocation) => {
            const employee = itm.assignedEmployee;
            if (employee) {
              const fullName = `${employee.firstName ?? ""} ${employee.lastName ?? ""}`.trim();
              if (fullName) {
                uniqueNames.add(fullName);
              }
            }
          });

          // Convert the Set back to an array
          const distinctNames = Array.from(uniqueNames);

          return (
            <>
              {(distinctNames ?? []).map((name: string, index: number) => (
                <p key={`asm_${index}`}>{name}</p>
              ))}
            </>
          );
        },
      },*/

      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <div className="flex justify-end gap-2">
            <div
              className="flex items-center justify-center size-8 transition-all duration-200 ease-linear rounded-md text-slate-500 bg-slate-100 hover:text-white hover:bg-slate-500 dark:text-zink-200 dark:bg-zink-600 dark:hover:text-white dark:hover:bg-zink-400 cursor-pointer"
              onClick={() => handleEditClick(cell.row.original.id)}
            >
              <Pencil className="size-4" />
            </div>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      <React.Fragment>
        <BreadCrumb title={title} pageTitle={pageTitle} />
        <div className="grid grid-cols-1 xl:grid-cols-12 gap-x-5 min-w-[50%]">
          <div className="xl:col-span-12">
            <div className="card">
              <div className="card-body">
                {loading ? (
                  <LoadingComponent></LoadingComponent>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-4 mb-5 lg:grid-cols-2 xl:grid-cols-12">
                      <div className="xl:col-span-4">
                        <div className="relative">
                          <input
                            type="text"
                            className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Search by Customer Name Email Phone"
                            autoComplete="off"
                            onChange={(e) => setSearchText(e.target.value)}
                            value={searchText}
                          />
                          <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                        </div>
                      </div>
                      <div className="xl:col-span-4">
                        <div className="relative">
                          <input
                            type="text"
                            className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                            placeholder="Vehicle number"
                            autoComplete="off"
                            onChange={(e) => setSearchVehicleText(e.target.value)}
                            value={searchVehicleText}
                          />
                          {/*   <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" /> */}
                        </div>
                      </div>

                      <div className="xl:col-span-2">
                        <div className="ltr:lg:text-right rtl:lg:text-left">
                          <Link
                            to=""
                            onClick={onSearchClick}
                            type="button"
                            className="text-white btn  min-w-40 bg-custom-800 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                          >
                            <span className="align-middle">Search</span>
                          </Link>
                        </div>
                      </div>
                      <div className="xl:col-span-2">
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              navigate(RoutePath.Admin_Job_create);
                            }}
                            className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                          >
                            Create a Job
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto mt-2">
                      {data && data.length > 0 ? (
                        <PagedTableContainer
                          isPagination={true}
                          columns={columns || []}
                          pagedData={data || []}
                          totalCount={total}
                          customPageSize={pageSize}
                          currentPage={currentPage}
                          divclassName="overflow-x-auto"
                          tableclassName="w-full whitespace-nowrap"
                          theadclassName="ltr:text-left rtl:text-right bg-slate-100 text-slate-500 dark:bg-zink-600 dark:text-zink-200"
                          thclassName="px-3.5 py-2.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                          tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                          PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                          onPageChanged={(pageIndex: number) => {
                            setCurrentPage(pageIndex + 1);
                          }}
                        />
                      ) : (
                        <div className="noresult">
                          <div className="py-6 text-center">
                            {/*   <Search className="size-6 mx-auto text-sky-500 fill-sky-100 dark:sky-500/20" /> */}
                            <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                            <p className="mb-0 text-slate-500 dark:text-zink-200">
                              We've searched jobs matched to your criteria, We did not find any.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    </>
  );
};

export default JobListPage;
